import { TextField } from '@mui/material';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { FormError } from './hook-form-error';

interface FormCurrencyInputProps {
  label: string;
  name: string;
  currencyChar?: string;
  disabled?: boolean;
}

interface FormatResponse {
  formattedValue?: string;
  originalValue?: string;
}

export const FormCurrencyInput = ({ name, label, currencyChar, disabled }: FormCurrencyInputProps): any => {
  const { control } = useFormContext();
  const { errors } = useFormState();

  const limitDecimalPlaces = (value: any) => {
    if (typeof value !== 'string') {
      throw new Error('Input must be a string');
    }

    // Find the decimal point
    const decimalIndex = value?.indexOf('.');

    if (decimalIndex === -1) {
      // If there's no decimal point, return the original value
      return value;
    }

    // Get the part after the decimal point
    const decimalPart = value.slice(decimalIndex + 1);

    if (decimalPart.length > 2) {
      // If more than two digits after the decimal point, truncate it
      const truncatedDecimalPart = decimalPart.slice(0, 2);
      return value.slice(0, decimalIndex + 1) + truncatedDecimalPart;
    }

    return value; // If there are 2 or fewer digits after the decimal, return the original value
  };

  const formatValue = (input?: string): FormatResponse => {
    if (!input) {
      return { formattedValue: '', originalValue: input };
    }
    const value = currencyChar ? String(input)?.replace(currencyChar, '') : input;

    // let sanitizedValue = value.replace(/[^0-9]/g, ''); // Allow only digits, removing everything else
    let sanitizedValue = value?.replace(/[^0-9.]/g, '');

    // Prevent negative values by ensuring it's positive
    // sanitizedValue = sanitizedValue === '' ? '' : String(Math.max(0, parseInt(sanitizedValue, 10)));
    // sanitizedValue = sanitizedValue.replace(/\.00$/, '');
    sanitizedValue = limitDecimalPlaces(sanitizedValue);

    return {
      formattedValue: `${currencyChar}${sanitizedValue}`,
      originalValue: sanitizedValue,
    };
  };

  return (
    <div className="form-group">
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <TextField
            className="formatted-input"
            variant="outlined"
            size="small"
            fullWidth
            label={label}
            disabled={disabled}
            value={formatValue(field.value).formattedValue}
            name={name}
            onChange={(e) => {
              field.onChange(formatValue(e.currentTarget.value).originalValue);
            }}
            onBlur={field.onBlur}
            placeholder="$0.00"
          />
        )}
      />
      <FormError name={name} />
    </div>
  );
};

FormCurrencyInput.defaultProps = {
  currencyChar: '$',
};
